import '../css/SubscribeForm.less';

import React, {
  useState,
  FunctionComponent,
  useCallback,
  useEffect,
} from 'react';
import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {
  Button,
  Input,
  Message,
  StrictInputProps,
  Checkbox,
} from 'semantic-ui-react';

import {
  useCreateStorageSubscriptionMutation,
  CreateStorageSubscriptionMutation,
  Maybe,
  Plan,
} from '../generated/graphql';
import SubscriptionAgreement from './SubscriptionAgreement';
import {
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
  doNotRetryContext,
} from '../util/errors';
import {captureError} from '../util/integrations';
import {ExecutionResult} from 'react-apollo';
import makeComp from '../util/profiler';

export interface ArtifactPricingPlan {
  id: string;
  name: string;
  billingInterval: string;
}

export interface StorageSubscribeFormProps {
  custoEmail: string;
  storageMonthly: Maybe<Plan>;
  trackingMonthly: Maybe<Plan>;
  orgID: string | undefined;
  onSubscriptionCompleted(success: boolean): void;
}

export const StorageSubscribeForm: FunctionComponent<StorageSubscribeFormProps> = makeComp(
  props => {
    const {
      storageMonthly,
      trackingMonthly,
      custoEmail,
      orgID,
      onSubscriptionCompleted,
    } = props;

    const [custoName, setCustoName] = useState('');
    const [agreementChecked, setAgreementChecked] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formDisabled, setFormDisabled] = useState(false);
    const [termsModalOpen, setTermsModalOpen] = useState(false);

    const units = 1;

    const stripe = useStripe();
    const elements = useElements();
    useEffect(() => {
      setFormDisabled(!stripe || !elements);
    }, [elements, stripe, setFormDisabled]);

    const [
      createStorageSubscriptionMutation,
    ] = useCreateStorageSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

    const onNameChanged = useCallback<
      NonNullable<StrictInputProps['onChange']>
    >(
      (event, data) => {
        setCustoName(data.value);
      },
      [setCustoName]
    );

    const storageID = storageMonthly?.id;
    const trackingID = trackingMonthly?.id;

    const handleSubscribe = useCallback(
      async (trial: boolean) => {
        if (!stripe || !elements) {
          throw new Error('Stripe not loaded.');
        }

        if (!agreementChecked) {
          setError('You must agree to the terms before subscribing.');
          return false;
        }

        const cardElement = elements.getElement(CardElement);
        if (orgID == null && cardElement == null) {
          throw new Error('Stripe card element not loaded.');
        }

        window.analytics.track('Storage subscribe purchase button clicked', {
          email: custoEmail,
        });

        const paymentMethod =
          cardElement != null
            ? await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                  name: custoName,
                  email: custoEmail,
                },
              })
            : null;

        if (
          paymentMethod != null &&
          (paymentMethod.error != null || paymentMethod.paymentMethod == null)
        ) {
          setError(
            paymentMethod.error?.message ?? 'Error validating payment method.'
          );
          return false;
        }

        if (storageID == null || trackingID == null) {
          return false;
        }

        let createSubResult: ExecutionResult<CreateStorageSubscriptionMutation>;

        const onError = (opts: {
          err: any;
          op: any;
          extra: {[key: string]: any};
          analyticsEvent: any;
          userFacingErrorMsg: any;
        }) => {
          captureError(opts.err, `StorageSubscribeForm-${opts.op}`, {
            extra: {email: custoEmail, units, trial, ...opts.extra},
          });
          if (opts.analyticsEvent != null) {
            window.analytics.track(opts.analyticsEvent, {
              email: custoEmail,
              units,
              trial,
              ...opts.extra,
            });
          }
          setError(opts.userFacingErrorMsg);
          onSubscriptionCompleted(/*success=*/ false);
        };

        try {
          const variables = {
            storagePlanId: storageID,
            trackingPlanId: trackingID,
            trial: false,
          };
          if (paymentMethod?.paymentMethod != null) {
            createSubResult = await createStorageSubscriptionMutation({
              variables: {
                ...variables,
                paymentMethod: paymentMethod.paymentMethod.id,
              },
            });
          } else {
            createSubResult = await createStorageSubscriptionMutation({
              variables: {
                ...variables,
                organizationId: orgID,
              },
            });
          }
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          onError({
            err,
            op: 'createSubResult',
            extra: {
              storageID,
              trackingID,
              reason: errMsg,
            },
            analyticsEvent: 'Error processing subscription payment',
            userFacingErrorMsg: `Error processing storage subscription payment: ${errMsg}`,
          });
          return false;
        }

        if (
          createSubResult.errors != null ||
          !createSubResult.data?.createStorageSubscription?.subscription
        ) {
          captureError(
            "Unknown error confirming subscription. Exception didn't throw but success was not reported.",
            'StorageSubscribeForm-handleSubscribe-unknown',
            {
              extra: {
                email: custoEmail,
                storageID,
                trackingID,
                units,
                trial,
              },
            }
          );
          setError(
            'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.'
          );
          onSubscriptionCompleted(/*success=*/ false);
          return false;
        }
        const {
          latest_invoice,
        } = createSubResult.data.createStorageSubscription.subscription;
        const {payment_intent} = latest_invoice;

        if (payment_intent) {
          const {client_secret, status} = payment_intent;
          if (
            status === 'requires_action' ||
            status === 'requires_payment_method'
          ) {
            try {
              const result = await stripe.confirmCardPayment(client_secret);
              if (result.error) {
                captureError(
                  result.error.message!,
                  'StorageSubscribeForm-handleCardPayment',
                  {
                    extra: {
                      email: custoEmail,
                      storageID,
                      trackingID,
                      units,
                      trial,
                    },
                  }
                );
                setError(
                  `We had trouble confirming your payment: ${result.error.message}`
                );
                onSubscriptionCompleted(/*success=*/ false);
                return false;
              }
            } catch (error) {
              captureError(error, 'StorageSubscribeForm-handleCardPayment', {
                extra: {
                  email: custoEmail,
                  storageID,
                  trackingID,
                  units,
                  trial,
                },
              });
              setError(`We had trouble confirming your payment: ${error}`);
              onSubscriptionCompleted(/*success=*/ false);
              return false;
            }
          }
        }

        onSubscriptionCompleted(/*success=*/ true);
        return true;
      },
      [
        stripe,
        elements,
        agreementChecked,
        custoEmail,
        storageID,
        trackingID,
        units,
        custoName,
        createStorageSubscriptionMutation,
        onSubscriptionCompleted,
        orgID,
      ]
    );

    const onSubscribe = useCallback(
      async (trial: boolean) => {
        // Clear status messages and disable further input.
        setError(null);
        setFormDisabled(true);

        const completedSuccessfully = await handleSubscribe(trial);

        // Make form editable again after results are in.
        if (!completedSuccessfully) {
          setFormDisabled(false);
        }
      },
      [handleSubscribe, setError, setFormDisabled]
    );

    return (
      <div className="checkout-form" style={{padding: '0'}}>
        {error != null && (
          <Message negative content={error} className="alert" />
        )}
        <div className="checkout-fields">
          <Input
            className="custo-input"
            placeholder="Email address"
            defaultValue={custoEmail}
            disabled
          />
          {orgID == null && (
            <>
              <Input
                data-test="cardholder-name-input"
                className="custo-input"
                placeholder="Name on card"
                onChange={onNameChanged}
                disabled={formDisabled}
              />
              <div className="card-element">
                <CardElement
                  options={{
                    disabled: formDisabled,
                    style: {
                      base: {
                        fontSize: '15px',
                        color: '#424770',
                        fontFamily: 'sans-serif',
                        fontWeight: '300',
                        letterSpacing: '0.025em',
                        '::placeholder': {
                          color: 'rgba(55, 59, 62, 0.45)',
                        },
                      },
                    },
                  }}
                />
              </div>
            </>
          )}

          <Checkbox
            onChange={(_1, data) => setAgreementChecked(data.checked ?? false)}
            label={
              <label>
                I agree to the{' '}
                <span
                  className="link-span"
                  onClick={e => {
                    setTermsModalOpen(true);
                    e.stopPropagation();
                  }}>
                  subscription agreement.
                </span>
              </label>
            }
            disabled={formDisabled}
          />
          <div className="subscribe-button-group">
            <Button
              className="subscribe-button"
              color="blue"
              onClick={() => onSubscribe(/*trial=*/ false)}
              disabled={formDisabled}>
              {formDisabled ? 'Processing...' : 'Subscribe now'}
            </Button>
          </div>
        </div>
        <SubscriptionAgreement
          open={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
        />
      </div>
    );
  },
  {id: 'StorageSubscribeForm'}
);
